import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import {
  Dimmer,
  Loader,
  Segment,
  Header,
  Icon,
  Card,
  Pagination,
  Container
} from 'semantic-ui-react';

import modes from './constants/modes';
import errors from './constants/errors';

import { makeGetWithParam } from './../../../services/ServiceManager';

import AssetPreview from './AssetPreview';

class AssetList extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      assets: [],
      error: {
        type: null, // UNABLE_TO_LOAD
        message: ''
      },
      selectedAssets: [],
      pagination: {
        activePage: 1,
        boundaryRange: 1,
        siblingRange: 1,
        showEllipsis: true,
        showFirstAndLastNav: true,
        totalPages: 50,
        rowsPerPage: 8
      },
      searchParams: {}
    };
  }

  componentDidMount() {
    const { inArchiveMode } = this.props;
    const { pagination } = this.state;
    const { activePage, rowsPerPage } = pagination;

    this.fetchAssets(
      `?activePage=${activePage}&rowsPerPage=${rowsPerPage}&archived=${inArchiveMode}`
    );
  }

  componentWillReceiveProps(nextProps) {
    const {
      uploadedAssets,
      updatedAssetTags,
      deletedAssets,
      searchParams: nextSearchParams,
      refreshAssetList
    } = nextProps;

    const { searchParams: existingSearchParams } = this.props;

    const { assetIds, tags } = updatedAssetTags;

    if (uploadedAssets && uploadedAssets.length > 0) {
      // convert asset.created_at to moment instance.
      const newUploadedAssets = uploadedAssets.map((asset) => {
        const { created_at: createdAt } = asset; // must exist

        return {
          ...asset,
          created_at: moment(createdAt)
        };
      });

      // remove newUploadedAssets.length number of data from the back of assets
      // and insert these in the front
      const { assets, pagination } = this.state;
      const { rowsPerPage } = pagination;

      const combinedList = newUploadedAssets
        .concat(assets)
        .slice(0, rowsPerPage);

      this.setState((prevState) => ({
        ...prevState,
        assets: combinedList.slice(0, 20) // assuming that 20 assets per page...
      }));
    } else if (assetIds && tags && assetIds.length > 0 && tags.length > 0) {
      const { assets } = this.state;

      const newAssets = assets.map((asset) => {
        if (assetIds.indexOf(asset.id) !== -1) {
          return {
            ...asset,
            tags
          };
        }

        return {
          ...asset
        };
      });

      this.setState({
        ...this.state,
        assets: newAssets
      });
    } else if (
      (deletedAssets && deletedAssets.length > 0) ||
      refreshAssetList
    ) {
      const { searchParams, pagination } = this.state;

      const searchQueryString = this.createQueryString(
        searchParams,
        pagination
      );

      this.fetchAssets(searchQueryString);
    } else if (!isEqual(existingSearchParams, nextSearchParams)) {
      // whatever in the nextSearchParams, replace the key and value in existingSearchParam
      const nextSearchParamKeys = Object.keys(nextSearchParams);

      const newSearchParams = {};

      for (const key of nextSearchParamKeys) {
        const currentValue = nextSearchParams[key];

        if (
          // check if currentValue is valid
          currentValue &&
          // check if currentValue is an array and not empty
          ((currentValue instanceof Array && currentValue.length > 0) ||
            // check if currentValue is string and not empty
            (!(currentValue instanceof Array) && currentValue !== ''))
        ) {
          newSearchParams[key] = currentValue;
        }
      }

      this.setState(
        {
          ...this.state,
          searchParams: newSearchParams
        },
        () => {
          const { searchParams, pagination } = this.state;

          const searchQueryString = this.createQueryString(
            searchParams,
            pagination
          );

          this.fetchAssets(searchQueryString);
        }
      );
    }
  }

  componentDidUpdate() {}

  /** on asset click event
   * @param {object} asset
   * @param {bool} selected This parameter is used for distinguish selected assets between NORMAL and EDITTING mode.
   *
   * NOTE
   *
   * If mode === 'NORMAL', and an asset is clicked, the selected parameter will always be true.
   * If mode === 'EDITTING', the selected parameter will depends on the checkbox status.
   */
  onAssetClick = (asset, selected) => {
    const { mode, selectedAssets } = this.props;
    let newSelectedAssets = [];

    if (mode === modes.ASSET_GALLERY.EDITTING) {
      if (selected) {
        newSelectedAssets = [...selectedAssets, asset];
      } else {
        newSelectedAssets = selectedAssets.filter(
          (selectedAsset) => selectedAsset.id !== asset.id
        );
      }
    } else {
      newSelectedAssets = [asset];
    }

    const { updateSelectedAssets } = this.props;

    if (updateSelectedAssets) updateSelectedAssets(newSelectedAssets); // disable -> true, enable -> false
  };

  onPageChange = (e, { activePage }) => {
    const { pagination, searchParams } = this.state;
    const { activePage: prevActivePage } = pagination;

    // return if same page has been clicked.
    // eslint-disable-next-line
    if (activePage == prevActivePage) return;

    this.fetchAssets(
      this.createQueryString(searchParams, { ...pagination, activePage })
    );
  };

  /**
   * This function is reponsible for creating query string like ?filename=10.mp4&oem[0]=VW&oem[1]=audi...
   * At the end of the query string, the pagination info will be attached..
   *
   * NOTE
   *
   * If the value for a key has empty value, remove this so that it does not affect the search results.
   * (ex. when user searched with filename in the beginning and deleted it, it should not search files with empty filename.)
   */
  createQueryString = (searchParams, pagination = null) => {
    const keys = Object.keys(searchParams);
    const { inArchiveMode } = this.props;
    const queryStrings = [];

    for (const key of keys) {
      const currentValue = searchParams[key];

      // need to determine if the value is an array or not.
      if (currentValue instanceof Array) {
        // ['oem[0]=1', 'oem[1]=2', ...]
        const listQueries = currentValue
          .map((value, index) => `${key}[${index}]=${value}`)
          .join('&');

        queryStrings.push(listQueries);
      } else {
        queryStrings.push(`${key}=${currentValue}`);
      }
    }

    if (pagination) {
      const { activePage, rowsPerPage } = pagination;

      return `?${queryStrings.join(
        '&'
      )}&activePage=${activePage}&rowsPerPage=${rowsPerPage}&archived=${inArchiveMode}`;
    }

    return `?${queryStrings.join('&')}`;
  };

  fetchAssets = (query = '') => {
    this.setState(
      {
        ...this.state,
        loading: true,
        assets: []
      },
      () => {
        let finalQuery = query;
        const { groups, email } = this.props.user;

        // This leaves room for possibility for future guests
        const applyGuestFilter = ['NU'].includes(groups[0]);

        if (applyGuestFilter && query) {
          finalQuery = `${query}&uploaded_by=${encodeURIComponent(email)}`;
        }

        makeGetWithParam('service/assets', null, finalQuery).then(
          (response) => {
            const { statusCode, result } = response;
            const { assets, activePage, totalPages } = result || {};
            const formattedAssetList = assets
              ? assets.map((asset) => ({
                  ...asset,
                  created_at: moment(asset.created_at) // created_at must exist, since in DB, the column is defaulted to now()
                }))
              : null;

            if (statusCode === 200) {
              this.setState((prevState) => ({
                ...prevState,
                loading: false,
                assets: formattedAssetList,
                error: {
                  type: null,
                  message: ''
                },
                pagination: {
                  ...prevState.pagination,
                  activePage,
                  totalPages
                }
              }));
            } else {
              this.setState((prevState) => ({
                ...prevState,
                loading: false,
                error: {
                  type: errors.ASSET_LIST.UNABLE_TO_LOAD,
                  message: 'Assets were not able to load. Please try again.'
                },
                pagination: {
                  ...prevState.pagination,
                  activePage,
                  totalPages
                }
              }));
            }

            const { updateSelectedAssets } = this.props;

            if (updateSelectedAssets) updateSelectedAssets([]); // This call does not depend on other state. Just call the function here.
          }
        );
      }
    );
  };

  toggleArchivedStatusForAsset = (assetId, assetFilename, archivedValue) => {
    const { toggleArchivedStatusForAsset } = this.props;
    if (toggleArchivedStatusForAsset) {
      toggleArchivedStatusForAsset(
        assetId,
        assetFilename,
        archivedValue,
        () => {
          const { searchParams, pagination } = this.state;
          const searchQueryString = this.createQueryString(
            searchParams,
            pagination
          );
          this.fetchAssets(searchQueryString);
        }
      );
    }
  };

  render() {
    const { loading, error, assets, pagination } = this.state;
    const {
      activePage,
      boundaryRange,
      siblingRange,
      showEllipsis,
      showFirstAndLastNav,
      totalPages
    } = pagination;

    const {
      tagOptions,
      updateTagOptions,
      mode,
      selectedAssets,
      updateMultipleTagOptions,
      inArchiveMode,
      user
    } = this.props; // passing tagOptions for each asset might not be neccessary. Change if it slows the app later.

    const selectedAssetIds = selectedAssets.map((asset) => asset.id);

    return (
      <Segment
        style={{
          height: '40em',
          zIndex: 1,
          overflowY: 'auto'
        }}
      >
        {loading && (
          <Dimmer active inverted>
            <Loader inverted size="big" content="Loading..." />
          </Dimmer>
        )}

        {!loading && error && error.type && (
          <Header
            icon
            as="h2"
            textAlign="center"
            style={{
              position: 'relative',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <Icon name="frown outline" />
            <Header.Content>{error.message}</Header.Content>
          </Header>
        )}

        {!loading && error && !error.type && assets.length === 0 && (
          <Header
            icon
            textAlign="center"
            style={{
              position: 'relative',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <Icon name="file image outline" />
            <Header.Content>No results</Header.Content>
          </Header>
        )}

        {!loading && error && !error.type && (
          <Card.Group itemsPerRow={4}>
            {assets.map((asset) => {
              const { id } = asset;
              return (
                <AssetPreview
                  key={id}
                  asset={asset}
                  tagOptions={tagOptions}
                  updateTagOptions={updateTagOptions}
                  mode={mode}
                  onAssetClick={this.onAssetClick}
                  selected={selectedAssetIds.indexOf(id) !== -1}
                  updateSelectedAssets={this.updateSelectedAssets}
                  updateAssetTags={this.props.updateAssetTags}
                  updateMultipleTagOptions={updateMultipleTagOptions}
                  inArchiveMode={inArchiveMode}
                  toggleArchivedStatusForAsset={
                    this.toggleArchivedStatusForAsset
                  }
                  user={user}
                />
              );
            })}
          </Card.Group>
        )}

        {!loading && error && !error.type && assets.length > 0 && (
          <Container fluid textAlign="center">
            <Pagination
              activePage={activePage}
              boundaryRange={boundaryRange}
              onPageChange={this.onPageChange}
              siblingRange={siblingRange}
              totalPages={totalPages}
              ellipsisItem={showEllipsis ? undefined : null}
              firstItem={showFirstAndLastNav ? undefined : null}
              lastItem={showFirstAndLastNav ? undefined : null}
              prevItem={null}
              nextItem={null}
              style={{ marginTop: '2.5em', marginBottom: '1em' }}
            />
          </Container>
        )}
      </Segment>
    );
  }
}

AssetList.defaultProps = {
  uploadedAssets: []
};

AssetList.propTypes = {
  tagOptions: PropTypes.object.isRequired,
  updateTagOptions: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  uploadedAssets: PropTypes.arrayOf(PropTypes.object),
  updateSelectedAssets: PropTypes.func.isRequired,
  updateAssetTags: PropTypes.func.isRequired,
  updateMultipleTagOptions: PropTypes.func.isRequired
};

export default AssetList;



// WEBPACK FOOTER //
// ./src/components/common/AssetGalleryV2/AssetList.jsx